import { useStore } from '../js/store';
import {
  LS_SECTION_KEYS,
  storeLocalData,
  retrieveLocalData,
} from './../js/localData';

const LS_SECTION_KEY = LS_SECTION_KEYS.ALERTS;
const DEFAULT_ALERTS_STATE =  {
  alertHistory: [],
  activeAlerts: [],
};

const currentAlerts = retrieveLocalData(LS_SECTION_KEY, Object.assign({}, DEFAULT_ALERTS_STATE));
// clear any active alerts that may be holdovers from last session
currentAlerts.activeAlerts = [];
// set local storage
storeLocalData(LS_SECTION_KEY, currentAlerts);

let DEFAULT_ALERT_PROPS = {
  alertBodyText: undefined,
  alertLevel: undefined,
  alertLink: undefined,
  alertLinkLabel: undefined,
  alertHeadingText: undefined,
  autoCloseTimeout: 5, // 5 seconds default timeout
  isPersistent: false,
  type: 'other'
};
// objAlertItem is expected to look like this for proper rendering by alertList/alertItem
// {
//   alertBodyText: detailed information on the alert,
//   alertLevel: oneOf(['error', 'warn', 'info']), sets color of alertItem
//   alertLink: Link url to be rendered in alert details
//   alertLinkLabel: label to be rendered as a link
//   alertHeadingText: PropTypes.string.isRequired,
//   autoCloseTimeout: PropTypes.number,
//   isPersistent : PropTypes.bool,
//   type: PropTypes.string  // 'offline', 'other' for dismissing error when back online
// }.

const initialState = currentAlerts;

const useAlerts = () => {
  const [state, setState] = useStore();
  // Creates error alert based on specific errorData structure.
  // Wherein errorData: {
  //   code?     { number }   - some HTTP error code, e.g. 401,
  //   message   { string }   - E.g. 'some descriptive but short explanation',
  //   details?  { string[] } - Array of objects that may help understand what is happening,
  //   trace_id? { string }   - Id of object containing headers used to follow request chains through cpaas,
  //      e.g. '834e92ef-5bab-4c72-b354-f41f2a6d9ed1'
  //   autoCloseTimeout?  { number }   - warning timeout. Default 0 - for persistent alert,
  //   type?     { string } - Alert type. Currently used for 'login' only.
  // }.
  const createErrorAlert = ({ message:alertHeadingText, details, autoCloseTimeout, type }) => {
    if (alertHeadingText) {
      createAlert({
        alertHeadingText,
        alertBodyText: (details || []).join('. '),
        autoCloseTimeout: autoCloseTimeout || 0,
        isPersistent: !autoCloseTimeout,
        type,
      }, 'error');
    } else {
      console.warn('Attempt to create error alert with no input alert data');
    }
  };

  const createWarnAlert = ({ message:alertHeadingText, details, autoCloseTimeout, type }) => {
    if (alertHeadingText) {
      createAlert({
        alertHeadingText,
        alertBodyText: (details || []).join('. '),
        autoCloseTimeout: autoCloseTimeout || 0,
        isPersistent: !autoCloseTimeout,
        type,
      }, 'warn');
    } else {
      console.warn('Attempt to create warn alert with no input alert data');
    }
  };

  const createInfoAlert = ({ message:alertHeadingText, details, autoCloseTimeout, type }) => {
    if (alertHeadingText) {
      createAlert({
        alertHeadingText,
        alertBodyText: (details || []).join('. '),
        autoCloseTimeout: autoCloseTimeout || 0,
        isPersistent: !autoCloseTimeout,
        type,
      }, 'info');
    } else {
      console.warn('Attempt to create info alert with no input alert data');
    }
  };

  const createAlert = (newAlertProps, alertLevel) => setState(state => {
    const storedAlerts = retrieveLocalData(LS_SECTION_KEY, { ...DEFAULT_ALERTS_STATE });

    if (newAlertProps && alertLevel) {
      if (!newAlertProps.alertHeadingText) {
        // can't show error with no summary, so console log error and set summary
        // to indicate 'unknown error'
        newAlertProps.alertHeadingText = 'Unknown error';
      }
      const newAlertItem = {
        ...DEFAULT_ALERT_PROPS,
        ...newAlertProps,
        alertLevel,
        timestamp: Date.now(),
        id: Date.now(),
      };

      if (!storedAlerts.alertHistory || !Array.isArray(storedAlerts.alertHistory)) {
        storedAlerts.alertHistory = [];
      }

      if (storedAlerts.alertHistory.unshift(newAlertItem) > 50) {
        storedAlerts.alertHistory = storedAlerts.alertHistory.slice(0, 50);
      }

      storedAlerts.activeAlerts = [newAlertItem];
      storeLocalData(LS_SECTION_KEY, storedAlerts);

      return ({
        ...state,
        alerts: storedAlerts,
      });
    }
  });

  const removeSpecificAlert = (alertId = null) => setState(state => {
    if (alertId === null) {
      return state;
    } else {
      const storedAlerts = retrieveLocalData(LS_SECTION_KEY, { ...DEFAULT_ALERTS_STATE });

      storedAlerts.activeAlerts = (storedAlerts.activeAlerts || []).filter(({ id }) => id !== alertId);
      storeLocalData(LS_SECTION_KEY, storedAlerts);

      return ({
        ...state,
        alerts: storedAlerts,
      });
    }
  });

  const clearPersistentAlerts = () => setState(state => {
    const storedAlerts = retrieveLocalData(LS_SECTION_KEY, { ...DEFAULT_ALERTS_STATE });

    storedAlerts.activeAlerts = (storedAlerts.activeAlerts || []).filter(({ isPersistent }) => isPersistent === false);
    storeLocalData(LS_SECTION_KEY, storedAlerts);
    
    return ({
      ...state,
      alerts: storedAlerts,
    });
  });

  const clearAlerts = (clearType) => setState(state => {
    const storedAlerts = retrieveLocalData(LS_SECTION_KEY, { ...DEFAULT_ALERTS_STATE });

    if (clearType) {
      storedAlerts.activeAlerts = (storedAlerts.activeAlerts || []).filter(({ type }) => type !== clearType);
    }
    storeLocalData(LS_SECTION_KEY, storedAlerts);
    
    return ({
      ...state,
      alerts: storedAlerts,
    });
  });
  
  const getAlertsHistory = () => {
    const storedAlerts = retrieveLocalData(LS_SECTION_KEY, { ...DEFAULT_ALERTS_STATE });
    
    return storedAlerts.alertHistory || [];
  };

  const clearAlertsHistory = () => {
    const storedAlerts = retrieveLocalData(LS_SECTION_KEY, { ...DEFAULT_ALERTS_STATE });
    const newAlertsData = {
      activeAlerts: storedAlerts.activeAlerts || [],
      alertHistory: [],
    };

    storeLocalData(LS_SECTION_KEY, newAlertsData);
    return setState(state => ({
      ...state,
      alerts: {
        ...state.alerts,
        alertHistory: [],
      }
    }));
  };
  
  const aReturn = (state && state.alerts) || initialState;

  return [aReturn, {
    createErrorAlert,
    createWarnAlert,
    createInfoAlert,
    createAlert,
    removeSpecificAlert,
    clearPersistentAlerts,
    clearAlerts,
    getAlertsHistory,
    clearAlertsHistory,
  }];
};

export default useAlerts;
export { initialState };
